import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import {
  buildPrepForNewYearAccommodationHistory,
  getCampYear
} from '../../utils/accommodationHistoryUtils';

const PrepareForNewYear: React.FC = () => {
  const [confirmText, setConfirmText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // 0=not started, 1-6=step in progress/waiting, 7=complete
  const [step1Complete, setStep1Complete] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step3Complete, setStep3Complete] = useState(false);
  const [step4Complete, setStep4Complete] = useState(false);
  const [step5Complete, setStep5Complete] = useState(false);
  const [step6Complete, setStep6Complete] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [stepResults, setStepResults] = useState({
    step1: { registrations: 0 },
    step3: { campers: 0 },
    step4: { invoiceStart: 0 },
    step6: { deleted: 0 }
  });
  const [lastClosedYear, setLastClosedYear] = useState<number | null>(null);
  const [loadingLastYear, setLoadingLastYear] = useState(true);

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  useEffect(() => {
    const fetchLastClosedYear = async () => {
      try {
        const archiveSnapshot = await getDocs(collection(db, 'archive'));
        const years = archiveSnapshot.docs
          .map(doc => parseInt(doc.id))
          .filter(year => !isNaN(year))
          .sort((a, b) => b - a); // Sort descending
        
        if (years.length > 0) {
          setLastClosedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching last closed year:', error);
      } finally {
        setLoadingLastYear(false);
      }
    };

    fetchLastClosedYear();
  }, []);

  const resetSteps = () => {
    setCurrentStep(0);
    setStep1Complete(false);
    setStep2Complete(false);
    setStep3Complete(false);
    setStep4Complete(false);
    setStep5Complete(false);
    setStep6Complete(false);
    setStepResults({
      step1: { registrations: 0 },
      step3: { campers: 0 },
      step4: { invoiceStart: 0 },
      step6: { deleted: 0 }
    });
  };

  const runStep1 = async () => {
    setProcessing(true);
    setCurrentStep(1);
    setProgress({ current: 0, total: 0, message: 'Archiving registrations...' });

    try {
      // Check if archive already exists
      const archiveDocRef = doc(db, 'archive', String(currentYear));
      const archiveDocSnap = await getDoc(archiveDocRef);
      
      if (archiveDocSnap.exists()) {
        alert(`Archive for ${currentYear} already exists! This tool has already been run.`);
        setProcessing(false);
        setCurrentStep(0);
        return;
      }

      const registrationsSnapshot = await getDocs(collection(db, 'Registrations'));
      const totalRegistrations = registrationsSnapshot.size;
      
      setProgress({ current: 0, total: totalRegistrations, message: 'Archiving registrations...' });

      // Create archive document with metadata
      await setDoc(archiveDocRef, {
        year: currentYear,
        archivedAt: new Date(),
        recordCount: totalRegistrations
      });

      let archived = 0;
      for (const regDoc of registrationsSnapshot.docs) {
        const archiveRegRef = doc(db, 'archive', String(currentYear), 'registrations', regDoc.id);
        await setDoc(archiveRegRef, regDoc.data());
        archived++;
        setProgress({ current: archived, total: totalRegistrations, message: 'Archiving registrations...' });
      }

      setStep1Complete(true);
      setStepResults(prev => ({ ...prev, step1: { registrations: totalRegistrations } }));
      setLastClosedYear(currentYear); // Update the last closed year display
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error in Step 1:', error);
      alert('Error archiving registrations: ' + (error as Error).message);
      setCurrentStep(0);
    } finally {
      setProcessing(false);
    }
  };

  const runStep2 = async () => {
    setProcessing(true);
    setCurrentStep(2);
    setProgress({ current: 0, total: 0, message: 'Updating accommodations...' });

    try {
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const inventorySnap = await getDoc(inventoryRef);
      
      if (inventorySnap.exists()) {
        const inventoryData = inventorySnap.data();
        const accommodationTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];
        
        let updatesNeeded = false;
        for (const type of accommodationTypes) {
          if (inventoryData[type] && Array.isArray(inventoryData[type])) {
            inventoryData[type] = inventoryData[type].map((item: any) => {
              if (item.status === 'occupied') {
                updatesNeeded = true;
                return { ...item, status: 'available' };
              }
              return item;
            });
          }
        }
        
        if (updatesNeeded) {
          await setDoc(inventoryRef, inventoryData);
        }
      }

      setStep2Complete(true);
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error in Step 2:', error);
      alert('Error updating accommodations: ' + (error as Error).message);
      setCurrentStep(1);
    } finally {
      setProcessing(false);
    }
  };

  const runStep3 = async () => {
    setProcessing(true);
    setCurrentStep(3);
    setProgress({ current: 0, total: 0, message: 'Updating primary camper history...' });

    try {
      const campYear = await getCampYear();
      const followingYear = campYear + 1;
      const primaryCampersSnapshot = await getDocs(collection(db, 'primaryCampers'));
      const totalCampers = primaryCampersSnapshot.size;
      
      setProgress({
        current: 0,
        total: totalCampers,
        message: `Adding ${followingYear} stay history placeholder...`
      });

      let updated = 0;
      for (const camperDoc of primaryCampersSnapshot.docs) {
        const camperData = camperDoc.data();
        const history = buildPrepForNewYearAccommodationHistory(
          camperData.accommodationHistory || [],
          followingYear
        );

        const camperDocRef = doc(db, 'primaryCampers', camperDoc.id);
        await updateDoc(camperDocRef, { accommodationHistory: history });
        
        updated++;
        setProgress({
          current: updated,
          total: totalCampers,
          message: `Adding ${followingYear} stay history placeholder...`
        });
      }

      setStep3Complete(true);
      setStepResults(prev => ({ ...prev, step3: { campers: totalCampers } }));
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error in Step 3:', error);
      alert('Error updating primary camper history: ' + (error as Error).message);
      setCurrentStep(2);
    } finally {
      setProcessing(false);
    }
  };

  const runStep4 = async () => {
    setProcessing(true);
    setCurrentStep(4);
    setProgress({ current: 0, total: 0, message: 'Resetting invoice counter...' });

    try {
      const newInvoiceStart = nextYear * 10000 + 1000;
      const counterRef = doc(db, 'settings', 'invoiceCounter');
      await setDoc(counterRef, { lastInvoiceNumber: newInvoiceStart });

      setStep4Complete(true);
      setStepResults(prev => ({ ...prev, step4: { invoiceStart: newInvoiceStart } }));
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error in Step 4:', error);
      alert('Error resetting invoice counter: ' + (error as Error).message);
      setCurrentStep(3);
    } finally {
      setProcessing(false);
    }
  };

  const runStep5 = async () => {
    setProcessing(true);
    setCurrentStep(5);
    setProgress({ current: 0, total: 0, message: 'Updating camp year...' });

    try {
      const campSettingsRef = doc(db, 'settings', 'camp');
      await updateDoc(campSettingsRef, { year: nextYear });

      setStep5Complete(true);
      setProgress({ current: 0, total: 0, message: '' });
    } catch (error) {
      console.error('Error in Step 5:', error);
      alert('Error updating camp year: ' + (error as Error).message);
      setCurrentStep(4);
    } finally {
      setProcessing(false);
    }
  };

  const runStep6 = async () => {
    setProcessing(true);
    setCurrentStep(6);
    setProgress({ current: 0, total: 0, message: 'Deleting old registrations...' });

    try {
      const { deleteDoc } = await import('firebase/firestore');
      const registrationsSnapshot = await getDocs(collection(db, 'Registrations'));
      const totalToDelete = registrationsSnapshot.size;
      
      setProgress({ current: 0, total: totalToDelete, message: 'Deleting old registrations...' });

      let deleted = 0;
      for (const regDoc of registrationsSnapshot.docs) {
        await deleteDoc(regDoc.ref);
        deleted++;
        setProgress({ current: deleted, total: totalToDelete, message: 'Deleting old registrations...' });
      }

      setStep6Complete(true);
      setStepResults(prev => ({ ...prev, step6: { deleted: totalToDelete } }));
      setCurrentStep(7); // All done
      setProgress({ current: 0, total: 0, message: '' });

      alert(
        `System prepared for ${nextYear}!\n\n` +
        `✓ ${stepResults.step1.registrations} registrations archived\n` +
        `✓ Accommodations reset to available\n` +
        `✓ ${stepResults.step3.campers} primary camper records updated\n` +
        `✓ Invoice counter reset to ${stepResults.step4.invoiceStart}\n` +
        `✓ Camp year updated to ${nextYear}\n` +
        `✓ ${totalToDelete} old registrations deleted`
      );
    } catch (error) {
      console.error('Error in Step 6:', error);
      alert('Error deleting old registrations: ' + (error as Error).message);
      setCurrentStep(5);
    } finally {
      setProcessing(false);
    }
  };

  const handleStartProcess = () => {
    if (!window.confirm(
      `This will prepare the system for ${nextYear}. This is a major operation that will:\n\n` +
      `1. Archive all ${currentYear} registrations\n` +
      `2. Update accommodation statuses\n` +
      `3. Add ${nextYear} stay history placeholder for all campers\n` +
      `4. Reset the invoice counter\n` +
      `5. Update the camp year setting\n` +
      `6. Delete old registrations\n\n` +
      `You will be able to verify each step before continuing.\n\n` +
      `Are you absolutely sure you want to continue?`
    )) {
      return;
    }

    resetSteps();
    runStep1();
  };

  const handleContinue = () => {
    if (currentStep === 1 && step1Complete) runStep2();
    else if (currentStep === 2 && step2Complete) runStep3();
    else if (currentStep === 3 && step3Complete) runStep4();
    else if (currentStep === 4 && step4Complete) runStep5();
    else if (currentStep === 5 && step5Complete) runStep6();
  };

  const canExecute = confirmText === `CONFIRM ${currentYear}` && !processing && currentStep === 0;
  const canContinue = !processing && currentStep > 0 && currentStep < 7 && 
                      ((currentStep === 1 && step1Complete) ||
                       (currentStep === 2 && step2Complete) ||
                       (currentStep === 3 && step3Complete) ||
                       (currentStep === 4 && step4Complete) ||
                       (currentStep === 5 && step5Complete));

  return (
    <div style={{ 
      marginTop: '2rem',
      padding: '1.5rem',
      border: '3px solid #dc3545',
      borderRadius: '8px',
      backgroundColor: '#fff5f5'
    }}>
      <h3 style={{ 
        color: '#dc3545', 
        marginTop: 0,
        marginBottom: '1rem',
        fontSize: '1.3rem',
        fontWeight: 'bold'
      }}>
        ⚠️ Prepare for New Year ({nextYear})
      </h3>

      {/* Last Closed Year Info */}
      {!loadingLastYear && (
        <div style={{
          padding: '0.75rem',
          backgroundColor: lastClosedYear ? '#d4edda' : '#f8f9fa',
          border: '1px solid ' + (lastClosedYear ? '#28a745' : '#dee2e6'),
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.95rem',
          color: lastClosedYear ? '#155724' : '#6c757d'
        }}>
          <strong>Last year closed out:</strong> {lastClosedYear || 'None (never run)'}
        </div>
      )}

      <div style={{ 
        padding: '1rem',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '4px',
        marginBottom: '1.5rem',
        fontSize: '0.95rem'
      }}>
        <strong>Warning:</strong> This is a major operation that should only be run ONCE at the end of the camping season.
        It will archive all {currentYear} data and prepare the system for {nextYear}.
        {currentStep > 0 && currentStep < 7 && (
          <div style={{ marginTop: '0.5rem', color: '#856404', fontWeight: 'bold' }}>
            ⏸️ Process paused. Verify the previous step in your database before continuing.
          </div>
        )}
      </div>

      {/* Steps with LEDs */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step1Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step1Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step1Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Archive {currentYear} Registrations</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step2Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step2Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step2Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Reset Accommodations to Available</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step3Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step3Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step3Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Add {nextYear} Stay History Placeholder</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step4Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step4Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step4Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Reset Invoice Counter</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step5Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step5Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step5Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Update Camp Year to {nextYear}</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: step6Complete ? '#28a745' : '#fff',
            border: '2px solid ' + (step6Complete ? '#28a745' : '#dee2e6'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            color: '#fff',
            fontWeight: 'bold'
          }}>
            {step6Complete && '✓'}
          </div>
          <span style={{ fontSize: '0.95rem' }}>Delete Old Registrations</span>
        </div>
      </div>

      {/* Progress */}
      {processing && progress.total > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', color: '#495057' }}>
            {progress.message} ({progress.current} / {progress.total})
          </div>
          <div style={{
            width: '100%',
            height: '20px',
            backgroundColor: '#e9ecef',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(progress.current / progress.total) * 100}%`,
              height: '100%',
              backgroundColor: '#28a745',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      {/* Confirmation Input - only show if not started */}
      {currentStep === 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '0.5rem',
            fontSize: '0.95rem',
            fontWeight: 'bold'
          }}>
            Type "CONFIRM {currentYear}" to enable execution:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={processing}
            placeholder={`CONFIRM ${currentYear}`}
            style={{
              width: '300px',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '2px solid #dee2e6',
              borderRadius: '4px'
            }}
          />
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {currentStep === 0 && (
          <button
            onClick={handleStartProcess}
            disabled={!canExecute}
            style={{
              padding: '1rem 2rem',
              backgroundColor: canExecute ? '#dc3545' : '#dee2e6',
              color: canExecute ? '#fff' : '#6c757d',
              border: 'none',
              borderRadius: '4px',
              cursor: canExecute ? 'pointer' : 'not-allowed',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            {processing ? 'Processing...' : `Start Process`}
          </button>
        )}

        {currentStep > 0 && currentStep < 7 && (
          <button
            onClick={handleContinue}
            disabled={!canContinue}
            style={{
              padding: '1rem 2rem',
              backgroundColor: canContinue ? '#28a745' : '#dee2e6',
              color: canContinue ? '#fff' : '#6c757d',
              border: 'none',
              borderRadius: '4px',
              cursor: canContinue ? 'pointer' : 'not-allowed',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}
          >
            {processing ? 'Processing...' : 'Continue to Next Step'}
          </button>
        )}

        {currentStep === 7 && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            border: '1px solid #28a745',
            borderRadius: '4px',
            color: '#155724',
            fontWeight: 'bold'
          }}>
            ✓ All steps completed successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default PrepareForNewYear;


